import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import { 
  PageHeader,
  SearchBar,
  ListContainer,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Button,
  FormSelect,
  FormCheckbox,
  ErrorMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge
} from '../components/common'
import ManageCategories from '../components/ManageCategories'

const StaticAssets = () => {
  const navigate = useNavigate()
  const [assets, setAssets] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedAssets, setSelectedAssets] = useState([])
  const [showManageCategories, setShowManageCategories] = useState(false)
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 12,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  })

  useEffect(() => {
    loadAssets()
    loadCategories()
  }, [pagination.pageNumber, pagination.pageSize, searchTerm, selectedCategory])

  const loadAssets = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        PageNumber: pagination.pageNumber,
        PageSize: pagination.pageSize,
        ...(searchTerm && { Condition: searchTerm }),
        ...(selectedCategory && { category: selectedCategory })
      }
      const response = await api.get('/StaticAssets', { params })
      setAssets(response.data.data || [])
      setPagination(prev => ({
        ...prev,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages,
        hasNextPage: response.data.hasNextPage,
        hasPreviousPage: response.data.hasPreviousPage
      }))
    } catch (err) {
      console.error('Failed to load assets:', err)
      setError(extractErrorMessage(err))
      setAssets([])
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      console.log('Loading asset categories...')
      const response = await api.get('/StaticAssets/categories')
      console.log('Categories loaded:', response.data)
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
      console.error('Categories API error:', err.response?.data)
      // Set default categories if API fails
      setCategories(['Images', 'Documents', 'Videos', 'Other'])
    }
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
    setPagination(prev => ({ ...prev, pageNumber: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, pageNumber: newPage }))
  }

  const handleUploadNew = () => {
    navigate('/static-assets/upload')
  }

  const handleCategoriesUpdated = () => {
    // Reload categories when they're updated
    loadCategories()
  }

  const handleAssetClick = (asset) => {
    navigate(`/static-assets/${asset.id}`)
  }

  const handleSelectAsset = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    )
  }

  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([])
    } else {
      setSelectedAssets(assets.map(asset => asset.id))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedAssets.length === 0) return
    
    if (window.confirm(`Are you sure you want to delete ${selectedAssets.length} selected asset(s)?`)) {
      try {
        await api.post('/StaticAssets/delete-multiple', selectedAssets)
        setSelectedAssets([])
        loadAssets()
      } catch (err) {
        showError(err)
      }
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return '🖼️'
    if (contentType?.includes('pdf')) return '📄'
    if (contentType?.startsWith('video/')) return '🎥'
    if (contentType?.startsWith('audio/')) return '🎵'
    return '📎'
  }

  if (loading && pagination.pageNumber === 1) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Static Assets"
        subtitle="Manage static content like images, documents, and other files stored in S3"
      />

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mb-6">
        {selectedAssets.length > 0 && (
          <Button
            onClick={handleDeleteSelected}
            variant="danger"
          >
            Delete Selected ({selectedAssets.length})
          </Button>
        )}
        <Button
          onClick={() => setShowManageCategories(true)}
          variant="secondary"
        >
          Manage Categories
        </Button>
        <Button
          onClick={handleUploadNew}
          variant="primary"
        >
          Upload New Asset
        </Button>
      </div>

      {error && (
        <ErrorMessage 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <SearchBar
            placeholder="Search assets by filename or description..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <FormSelect
          value={selectedCategory}
          onChange={(e) => {
            setSelectedCategory(e.target.value)
            setPagination(prev => ({ ...prev, pageNumber: 1 }))
          }}
          options={[
            { value: '', label: 'All Categories' },
            ...categories.map(category => ({ value: category, label: category }))
          ]}
          className="min-w-40"
        />
      </div>

      <ListContainer className="overflow-visible">
        {assets.length === 0 ? (
          <EmptyState 
            title="No static assets found"
            description="Upload your first asset to get started"
            actionText="Upload Asset"
            onAction={handleUploadNew}
          />
        ) : (
          <Table className="relative overflow-visible">
            <TableHeader>
              <TableRow>
                <TableCell header>
                  <FormCheckbox
                    checked={selectedAssets.length === assets.length && assets.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell header>File</TableCell>
                <TableCell header>Description</TableCell>
                <TableCell header>Category</TableCell>
                <TableCell header>Size</TableCell>
                <TableCell header>Created</TableCell>
                <TableCell header>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((asset) => (
                <TableRow key={asset.id} clickable onClick={() => handleAssetClick(asset)}>
                  <TableCell>
                    <FormCheckbox
                      checked={selectedAssets.includes(asset.id)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleSelectAsset(asset.id)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="relative mr-3 group">
                        {asset.contentType?.startsWith('image/') && asset.url ? (
                          <>
                            <img
                              src={asset.url}
                              alt={asset.fileName}
                              className="w-12 h-12 object-cover rounded border border-gray-200 transition-transform duration-200 group-hover:scale-105 cursor-pointer"
                              loading="lazy"
                              title="Hover to preview"
                            />
                            {/* Hover preview */}
                            <div className="absolute left-16 top-1/2 -translate-y-1/2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 z-[9999] pointer-events-none">
                              <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-3 transform scale-95 group-hover:scale-100 transition-transform duration-300">
                                <img
                                  src={asset.url}
                                  alt={asset.fileName}
                                  className="w-48 h-36 object-contain rounded"
                                  loading="lazy"
                                />
                                <div className="mt-2 text-xs text-gray-600 text-center max-w-48 truncate">
                                  {asset.fileName}
                                </div>
                                <div className="text-xs text-gray-500 text-center">
                                  {formatFileSize(asset.size)} • {asset.contentType}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                            <span className="text-xl">{getFileIcon(asset.contentType)}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={asset.fileName}>
                          {asset.fileName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {asset.contentType}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900 max-w-xs">
                      {asset.description ? (
                        <span className="line-clamp-2">{asset.description}</span>
                      ) : (
                        <span className="text-gray-400 italic">No description</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {asset.category ? (
                      <Badge variant="secondary" size="small">
                        {asset.category}
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">
                      {formatFileSize(asset.size)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">
                      {new Date(asset.createdAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAssetClick(asset)
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListContainer>

      {assets.length > 0 && (
        <Pagination
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          hasNext={pagination.hasNextPage}
          hasPrev={pagination.hasPreviousPage}
        />
      )}

      {/* Manage Categories Modal */}
      <ManageCategories
        isOpen={showManageCategories}
        onClose={() => setShowManageCategories(false)}
        onCategoriesUpdated={handleCategoriesUpdated}
      />
    </div>
  )
}

export default StaticAssets